import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../utils/api';
import {
  Activity,
  Plus,
  Calendar,
  Clock,
  Flag,
  User,
  Edit,
  Trash2,
  Eye,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  ArrowRight,
  UserCheck,
  Filter,
  Search,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import ActivityForm from '../../entities/Activities/ActivityForm';
import ActivityDetail from '../../entities/Activities/ActivityDetail';

const EntityActivities = ({ entityType, entityId, entityName }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [editingActivity, setEditingActivity] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    priority: '',
    completed: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  const loadActivities = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams({
        entityType,
        entityId,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '')
        )
      });

      const response = await fetch(`${api.baseURL}/api/activities/entity/${entityType}/${entityId}?${queryParams}`, {
        method: 'GET',
        headers: api.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to load activities');
      }

      const data = await response.json();
      setActivities(data.activities || []);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId, filters]);

  // Load activities for this entity
  useEffect(() => {
    console.log('EntityActivities useEffect triggered', { entityType, entityId });
    if (entityType && entityId) {
      console.log('Loading activities...');
      loadActivities();
    }
  }, [entityType, entityId, loadActivities]);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      search: '',
      status: '',
      priority: '',
      completed: ''
    });
  };

  // Handle activity actions
  const handleCreateActivity = () => {
    setEditingActivity(null);
    setShowForm(true);
  };

  const handleEditActivity = async (activity) => {
    try {
      // Fetch complete activity data including related entities
      const response = await fetch(`${api.baseURL}/api/activities/${activity.ActivityID}`, {
        headers: api.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch activity details');
      }

      const data = await response.json();
      
      setEditingActivity(data.activity);
      setShowForm(true);
    } catch (err) {
      console.error('Error fetching activity details:', err);
      setError(err.message);
      // Fallback to basic activity data
      setEditingActivity(activity);
      setShowForm(true);
    }
  };

  const handleViewActivity = (activity) => {
    setSelectedActivity(activity);
    setShowDetail(true);
  };

  const handleDeleteActivity = async (activityId) => {
    if (!window.confirm('Are you sure you want to delete this activity?')) {
      return;
    }

    try {
      const response = await fetch(`${api.baseURL}/api/activities/${activityId}`, {
        method: 'DELETE',
        headers: api.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to delete activity');
      }

      loadActivities();

    } catch (err) {
      setError(err.message);
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingActivity(null);
    loadActivities();
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Done': return 'text-green-600 bg-green-100';
      case 'In Progress': return 'text-blue-600 bg-blue-100';
      case 'Planned': return 'text-gray-600 bg-gray-100';
      case 'On Hold': return 'text-yellow-600 bg-yellow-100';
      case 'Cancelled': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'text-red-600';
      case 'High': return 'text-orange-600';
      case 'Medium': return 'text-blue-600';
      case 'Low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  // Get priority icon
  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'Critical': return <Flag className="h-3 w-3" fill="currentColor" />;
      case 'High': return <Flag className="h-3 w-3" fill="currentColor" />;
      case 'Medium': return <Flag className="h-3 w-3" />;
      case 'Low': return <Flag className="h-3 w-3" />;
      default: return <Flag className="h-3 w-3" />;
    }
  };

  // Check if activity is overdue
  const isOverdue = (dueDate, status) => {
    if (!dueDate || status === 'Done' || status === 'Cancelled') return false;
    return new Date(dueDate) < new Date();
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Activity className="h-5 w-5 text-gray-400" />
          <h3 className="text-lg font-medium">Activities</h3>
          <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-sm">
            {activities.length}
          </span>
        </div>
        <button
          onClick={handleCreateActivity}
          className="bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700 flex items-center space-x-1 text-sm"
        >
          <Plus className="h-4 w-4" />
          <span>New Activity</span>
        </button>
      </div>

      {/* Filters */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search activities..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center space-x-1 px-2 py-2 text-sm rounded-lg border transition-colors ${
                showFilters
                  ? 'bg-blue-100 text-blue-700 border-blue-300'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Filter className="h-4 w-4" />
              <span>Filters</span>
              {showFilters ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          </div>
          {Object.values(filters).some(v => v !== '') && (
            <button
              onClick={resetFilters}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              Clear All
            </button>
          )}
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 bg-gray-50 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="">All Statuses</option>
                <option value="Planned">Planned</option>
                <option value="In Progress">In Progress</option>
                <option value="Done">Done</option>
                <option value="On Hold">On Hold</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="">All Priorities</option>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Completion</label>
              <select
                value={filters.completed}
                onChange={(e) => handleFilterChange('completed', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="">All</option>
                <option value="false">Incomplete</option>
                <option value="true">Completed</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center mb-4">
          <AlertCircle className="h-4 w-4 text-red-500 mr-2" />
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      )}

      {/* Activities List */}
      <div className="space-y-3">
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <RefreshCw className="h-5 w-5 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Loading activities...</span>
          </div>
        ) : activities.length === 0 ? (
          <div className="text-center py-8">
            <Activity className="h-8 w-8 mx-auto text-gray-300 mb-2" />
            <p className="text-gray-500">No activities found</p>
            <p className="text-gray-400 text-sm mb-4">
              {Object.values(filters).some(v => v !== '')
                ? 'Try adjusting your filters'
                : `Create your first activity for this ${entityType.toLowerCase()}`
              }
            </p>
            <button
              onClick={handleCreateActivity}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2 mx-auto"
            >
              <Plus className="h-4 w-4" />
              <span>Create Activity</span>
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {activities.map((activity) => (
              <div
                key={activity.ActivityID}
                className={`border rounded-lg p-4 transition-colors hover:bg-gray-50 ${
                  isOverdue(activity.DueDate, activity.Status) ? 'border-red-200 bg-red-50' : 'border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      {activity.ParentActivityID && (
                        <ArrowRight className="h-4 w-4 text-gray-400" />
                      )}
                      <h4 className="font-medium text-gray-900">{activity.Subject}</h4>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(activity.Status)}`}>
                        {activity.Status}
                      </span>
                      <div className={`flex items-center space-x-1 ${getPriorityColor(activity.Priority)}`}>
                        {getPriorityIcon(activity.Priority)}
                        <span className="text-xs">{activity.Priority}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-500 mb-2">
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-4 w-4" />
                        <span>{activity.ActivityType}</span>
                      </div>
                      {activity.DueDate && (
                        <div className="flex items-center space-x-1">
                          <Clock className="h-4 w-4" />
                          <span className={isOverdue(activity.DueDate, activity.Status) ? 'text-red-600' : ''}>
                            Due: {formatDate(activity.DueDate)}
                          </span>
                        </div>
                      )}
                      {activity.AssignedToName && (
                        <div className="flex items-center space-x-1">
                          <UserCheck className="h-4 w-4" />
                          <span>{activity.AssignedToName}</span>
                        </div>
                      )}
                    </div>
                    
                    {activity.Description && (
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                        {activity.Description}
                      </p>
                    )}
                    
                    {isOverdue(activity.DueDate, activity.Status) && (
                      <div className="flex items-center space-x-1 text-red-600 text-sm">
                        <AlertCircle className="h-4 w-4" />
                        <span>Overdue</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleViewActivity(activity)}
                      className="text-blue-600 hover:text-blue-800 p-1 rounded"
                      title="View Details"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleEditActivity(activity)}
                      className="text-gray-600 hover:text-gray-800 p-1 rounded"
                      title="Edit"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteActivity(activity.ActivityID)}
                      className="text-red-600 hover:text-red-800 p-1 rounded"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {showForm && (
        <ActivityForm
          activity={editingActivity}
          onClose={() => {
            setShowForm(false);
            setEditingActivity(null);
          }}
          onSuccess={handleFormSuccess}
          // Pre-fill the entity information
          initialData={{
            relatedEntityType: entityType,
            relatedEntityId: entityId,
            entityName: entityName
          }}
        />
      )}

      {showDetail && selectedActivity && (
        <ActivityDetail
          activity={selectedActivity}
          onClose={() => {
            setShowDetail(false);
            setSelectedActivity(null);
          }}
          onEdit={() => {
            setShowDetail(false);
            handleEditActivity(selectedActivity);
          }}
          onDelete={() => {
            setShowDetail(false);
            handleDeleteActivity(selectedActivity.ActivityID);
          }}
        />
      )}
    </div>
  );
};

export default EntityActivities; 